/**
 * Admin-Reiter „Neue Nutzer“ (#116, architektur.md §7): Karte „Abgleich mit Entra ID“ (Zeitplan,
 * Hand-Start, letzter Lauf) und die Liste der neuen Nutzer mit Rollenvergabe und „Geprüft“.
 * Optik und Bedienung wie `admin-projekt-pruefungen.tsx`. Zeitplan ändern und „Jetzt ausführen“
 * darf nur ein Voll-Admin (Backend: `requireAdmin`); KuSe-Admins lesen und vergeben Rollen.
 */
import type {
  EntraAbgleichEinstellungen,
  NeuerNutzer,
  NeuerNutzerErledigtGrund,
  UpdateEntraAbgleichZeitplanInput,
} from '@projektverwaltung/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FunctionRoleEditor,
  useFunctionRoleMutations,
  useInvalidateNachRollenaenderung,
} from '../components/admin/function-role-editor.js';
import { useToast } from '../components/toast.js';
import { useConfirm } from '../components/v2/confirm-dialog.js';
import { ADMIN_LOADING_FALLBACK_ACTOR } from '../lib/admin-gating.js';
import { ApiError, api } from '../lib/api.js';
import { formatZeitpunkt } from '../lib/format-date.js';
import { anzeigeName } from '../lib/nutzer-anzeige.js';
import { useSession } from '../lib/session.js';

const ABGLEICH_KEY = ['entra-abgleich'] as const;
const LISTE_KEY = ['neue-nutzer'] as const;
// Anzeige Mo…So; Werte nach JS Date.getDay() (0=So … 6=Sa).
const TAG_REIHENFOLGE = [1, 2, 3, 4, 5, 6, 0] as const;

export function NeueNutzerAdmin() {
  const { t } = useTranslation();
  const { user: sessionUser } = useSession();
  const actor = sessionUser ?? ADMIN_LOADING_FALLBACK_ACTOR;
  // Das Backend erzwingt es mit requireAdmin; hier nur, damit kein Knopf angeboten wird, der 403 liefert.
  const istAdmin = actor.is_admin;
  return (
    <section className="space-y-4">
      <div>
        <h2 className="font-display text-lg font-semibold text-[color:var(--ink)]">
          {t('admin.neue_nutzer.titel', 'Neue Nutzer')}
        </h2>
        <p className="text-xs text-[color:var(--ink-3)] mt-0.5">
          {t(
            'admin.neue_nutzer.untertitel',
            'Nutzer, die neu angelegt oder reaktiviert wurden und noch eine Rolle brauchen oder geprüft werden müssen.',
          )}
        </p>
      </div>
      <AbgleichKarte istAdmin={istAdmin} />
      <NeueNutzerListe />
    </section>
  );
}

function AbgleichKarte({ istAdmin }: { istAdmin: boolean }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirm();
  const query = useQuery({
    queryKey: ABGLEICH_KEY,
    queryFn: api.getEntraAbgleich,
    // Während ein Lauf läuft, alle 5 s nachsehen — danach nicht mehr.
    refetchInterval: (q) => (q.state.data?.lauf_laeuft_seit ? 5000 : false),
  });
  const [draft, setDraft] = useState<UpdateEntraAbgleichZeitplanInput | null>(null);
  useEffect(() => {
    if (query.data && draft === null) {
      const { enabled, time, weekdays } = query.data;
      setDraft({ enabled, time, weekdays });
    }
  }, [query.data, draft]);

  // Ein beobachteter Lauf (selbst gestartet oder beim Laden schon laufend) ist beendet, sobald
  // `lauf_laeuft_seit` wieder leer ist — dann zeigen die Listen die Ergebnisse (neue Nutzer, inaktive).
  const lauf_beobachtet = useRef(false);
  const d = query.data;
  useEffect(() => {
    if (!d) return;
    if (d.lauf_laeuft_seit) {
      lauf_beobachtet.current = true;
      return;
    }
    if (lauf_beobachtet.current) {
      lauf_beobachtet.current = false;
      for (const key of ['neue-nutzer', 'admin-users', 'users-all']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
    }
  }, [d, queryClient]);

  const speichern = useMutation({
    mutationFn: (body: UpdateEntraAbgleichZeitplanInput) => api.putEntraZeitplan(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ABGLEICH_KEY });
      toast.success(t('admin.neue_nutzer.gespeichert', 'Zeitplan gespeichert'));
    },
    onError: (err) =>
      toast.error(
        t('admin.neue_nutzer.speichern_fehler', 'Speichern fehlgeschlagen'),
        err instanceof Error ? err.message : undefined,
      ),
  });
  const lauf = useMutation({
    mutationFn: (probelauf: boolean) => api.starteEntraAbgleich(probelauf),
    onSuccess: (_daten, probelauf) => {
      lauf_beobachtet.current = true;
      toast.success(
        probelauf
          ? t(
              'admin.neue_nutzer.probelauf_gestartet',
              'Probelauf gestartet — es wird nichts geändert',
            )
          : t('admin.neue_nutzer.gestartet', 'Entra-Abgleich gestartet'),
      );
      void queryClient.invalidateQueries({ queryKey: ABGLEICH_KEY });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.push({
          kind: 'info',
          title: t('admin.neue_nutzer.laeuft_bereits', 'Es läuft bereits ein Entra-Abgleich.'),
        });
        void queryClient.invalidateQueries({ queryKey: ABGLEICH_KEY });
        return;
      }
      toast.error(
        t('admin.neue_nutzer.start_fehler', 'Start fehlgeschlagen'),
        err instanceof Error ? err.message : undefined,
      );
    },
  });

  const zeit = (iso: string | null) => (iso ? formatZeitpunkt(iso, i18n.language) : '–');
  const tagLabel: Record<number, string> = {
    0: t('admin.weekday_short_sun', 'So'),
    1: t('admin.weekday_short_mon', 'Mo'),
    2: t('admin.weekday_short_tue', 'Di'),
    3: t('admin.weekday_short_wed', 'Mi'),
    4: t('admin.weekday_short_thu', 'Do'),
    5: t('admin.weekday_short_fri', 'Fr'),
    6: t('admin.weekday_short_sat', 'Sa'),
  };
  const erg = d?.letzter_lauf_ergebnis ?? null;
  const laeuft = !!d?.lauf_laeuft_seit;

  return (
    <div className="rounded-lg border border-[color:var(--line)] bg-[var(--surface)] p-4 space-y-4">
      <div className="text-xs uppercase tracking-wide text-[color:var(--ink-3)]">
        {t('admin.neue_nutzer.abgleich_titel', 'Abgleich mit Entra ID')}
      </div>
      {draft !== null && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-xs text-[color:var(--ink-3)]">
              <input
                type="checkbox"
                checked={draft.enabled}
                disabled={!istAdmin}
                onChange={(e) =>
                  setDraft((x) => (x === null ? x : { ...x, enabled: e.target.checked }))
                }
              />
              {t('admin.neue_nutzer.auto_aktiv', 'Wöchentlich automatisch')}
            </label>
            <label className="flex items-center gap-2 text-xs text-[color:var(--ink-3)]">
              {t('admin.neue_nutzer.uhrzeit', 'Uhrzeit')}
              <input
                type="time"
                value={draft.time}
                disabled={!istAdmin}
                onChange={(e) => setDraft((x) => (x === null ? x : { ...x, time: e.target.value }))}
                className="rounded-md border border-[color:var(--line)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[color:var(--ink)] disabled:opacity-60"
              />
              {d && <span className="text-[color:var(--ink-4)]">({d.timezone})</span>}
            </label>
            <fieldset className="flex items-center gap-2 text-xs text-[color:var(--ink-3)]">
              <legend className="sr-only">{t('admin.neue_nutzer.tage', 'Wochentage')}</legend>
              <span aria-hidden="true">{t('admin.neue_nutzer.tage', 'Wochentage')}</span>
              <div className="flex gap-2">
                {TAG_REIHENFOLGE.map((tag) => (
                  <label key={tag} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={draft.weekdays.includes(tag)}
                      disabled={!istAdmin}
                      onChange={() =>
                        setDraft((x) =>
                          x === null
                            ? x
                            : {
                                ...x,
                                weekdays: x.weekdays.includes(tag)
                                  ? x.weekdays.filter((w) => w !== tag)
                                  : [...x.weekdays, tag],
                              },
                        )
                      }
                    />
                    {tagLabel[tag]}
                  </label>
                ))}
              </div>
            </fieldset>
            {istAdmin && (
              <div className="ml-auto flex items-center gap-3">
                {draft.weekdays.length === 0 && (
                  <span className="text-xs text-[color:var(--warn)]">
                    {t(
                      'admin.neue_nutzer.kein_tag',
                      'Mindestens ein Wochentag nötig — zum Abschalten den Schalter nutzen.',
                    )}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => speichern.mutate(draft)}
                  disabled={speichern.isPending || draft.weekdays.length === 0}
                  className="rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                >
                  {t('admin.neue_nutzer.speichern', 'Speichern')}
                </button>
              </div>
            )}
          </div>
          {!istAdmin && (
            <p className="text-[11px] text-[color:var(--ink-4)] italic">
              {t('admin.neue_nutzer.nur_lesen', 'Den Zeitplan können nur Administratoren ändern.')}
            </p>
          )}
        </div>
      )}

      <div className="border-t border-[color:var(--line)] pt-3 flex flex-wrap items-start gap-4">
        <div className="space-y-1 text-sm text-[color:var(--ink-2)]">
          <div className="text-xs uppercase tracking-wide text-[color:var(--ink-3)]">
            {t('admin.neue_nutzer.letzter_lauf', 'Letzter Lauf')}
          </div>
          {laeuft && d?.lauf_laeuft_seit && (
            <div>
              {t('admin.neue_nutzer.laeuft_seit', 'Läuft seit {{zeit}}', {
                zeit: zeit(d.lauf_laeuft_seit),
              })}
            </div>
          )}
          {!d?.letzter_lauf_start ? (
            <div>{t('admin.neue_nutzer.nie', 'Noch kein Lauf.')}</div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span>
                  {t('admin.neue_nutzer.lauf_zeit', '{{start}} – {{ende}}', {
                    start: zeit(d.letzter_lauf_start),
                    ende: zeit(d.letzter_lauf_ende),
                  })}
                </span>
                {erg && <StatusBadge status={erg.status} />}
                {erg?.probelauf && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border border-[color:var(--primary)] text-[color:var(--primary)]">
                    {t('admin.neue_nutzer.probelauf_badge', 'Probelauf')}
                  </span>
                )}
              </div>
              {erg && <Kennzahlen erg={erg} />}
              {erg && (
                <div className="space-y-1">
                  <NamenListe
                    titel={t('admin.neue_nutzer.namen_neu', 'Neu angelegt')}
                    namen={erg.namen.neu}
                  />
                  <NamenListe
                    titel={t('admin.neue_nutzer.namen_reaktiviert', 'Reaktiviert')}
                    namen={erg.namen.reaktiviert}
                  />
                  <NamenListe
                    titel={t('admin.neue_nutzer.namen_deaktiviert', 'Deaktiviert')}
                    namen={erg.namen.deaktiviert}
                  />
                  <NamenListe
                    titel={t(
                      'admin.neue_nutzer.namen_ausgesetzt',
                      'Nicht deaktiviert (ausgesetzt)',
                    )}
                    namen={erg.namen.deaktivierung_ausgesetzt}
                    hinweis={t(
                      'admin.neue_nutzer.ausgesetzt_hinweis',
                      'Nicht deaktiviert, weil mehr als {{max}} betroffen wären — prüfen; Grenze über ENTRA_USER_LOAD_MAX_DEAKTIVIERUNGEN.',
                      { max: d.max_deaktivierungen },
                    )}
                    warnung
                  />
                </div>
              )}
              {erg && erg.fehler.length > 0 && (
                <div className="text-[color:var(--danger)]">
                  <div className="text-xs">{t('admin.neue_nutzer.fehler_titel', 'Fehler')}</div>
                  <ul className="text-xs">
                    {erg.fehler.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
        <div className="ml-auto text-right">
          {d && !d.konfiguriert ? (
            <p className="text-xs text-[color:var(--warn)]">
              {t(
                'admin.neue_nutzer.nicht_konfiguriert',
                'Nicht konfiguriert: ENTRA_USER_LOAD_GROUP_IDS und die Werte der Login-App (AZURE_TENANT_ID, AZURE_CLIENT_ID, AZURE_CLIENT_SECRET) müssen gesetzt sein.',
              )}
            </p>
          ) : (
            istAdmin && (
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={() => lauf.mutate(true)}
                  disabled={lauf.isPending || laeuft}
                  className="rounded-md border border-[color:var(--line)] px-3 py-1.5 text-sm text-[color:var(--ink-2)] disabled:opacity-50"
                >
                  {t('admin.neue_nutzer.probelauf', 'Probelauf (ändert nichts)')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Der echte Lauf ändert Nutzer unumkehrbar → Rückfrage; der Probelauf nicht.
                    void confirmAction({
                      title: t(
                        'admin.neue_nutzer.bestaetigen_titel',
                        'Abgleich wirklich ausführen?',
                      ),
                      body: t(
                        'admin.neue_nutzer.bestaetigen_text',
                        'Legt neue Nutzer an, aktualisiert Namen/E-Mail, deaktiviert Ausgeschiedene (Rollen werden entzogen) und überführt den QM-Alttext. Das lässt sich nicht rückgängig machen. Vorher einen Probelauf machen.',
                      ),
                      tone: 'danger',
                      confirmLabel: t('admin.neue_nutzer.bestaetigen_knopf', 'Ausführen'),
                    }).then((ok) => {
                      if (ok) lauf.mutate(false);
                    });
                  }}
                  disabled={lauf.isPending || laeuft}
                  className="rounded-md border border-[color:var(--line)] px-3 py-1.5 text-sm disabled:opacity-50"
                >
                  {t('admin.neue_nutzer.jetzt', 'Jetzt ausführen')}
                </button>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

type Ergebnis = NonNullable<EntraAbgleichEinstellungen['letzter_lauf_ergebnis']>;

function StatusBadge({ status }: { status: Ergebnis['status'] }) {
  const { t } = useTranslation();
  const label: Record<Ergebnis['status'], string> = {
    ok: t('admin.neue_nutzer.status_ok', 'erfolgreich'),
    teilweise: t('admin.neue_nutzer.status_teilweise', 'teilweise'),
    abgebrochen: t('admin.neue_nutzer.status_abgebrochen', 'abgebrochen'),
    nicht_konfiguriert: t('admin.neue_nutzer.status_nicht_konfiguriert', 'nicht konfiguriert'),
  };
  // ok = ruhig/grün, teilweise = Warnung (prüfen), abgebrochen/nicht konfiguriert = Gefahr.
  const farbe: Record<Ergebnis['status'], string> = {
    ok: 'bg-[var(--success-soft)] text-[color:var(--success)]',
    teilweise: 'bg-[var(--warn-soft)] text-[color:var(--warn)]',
    abgebrochen: 'bg-[var(--danger-soft)] text-[color:var(--danger)]',
    nicht_konfiguriert: 'bg-[var(--danger-soft)] text-[color:var(--danger)]',
  };
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${farbe[status]}`}>
      {label[status]}
    </span>
  );
}

function Kennzahlen({ erg }: { erg: Ergebnis }) {
  const { t } = useTranslation();
  const u = erg.uebersprungen;
  const summe =
    u.gast + u.extern + u.ohne_mail + u.ohne_personenname + u.deaktiviert_in_entra + u.testnutzer;
  const gruende: Array<[string, string, number]> = [
    ['gast', t('admin.neue_nutzer.ue_gast', 'Gäste'), u.gast],
    ['extern', t('admin.neue_nutzer.ue_extern', 'Externe Domäne'), u.extern],
    ['ohne_mail', t('admin.neue_nutzer.ue_ohne_mail', 'Ohne E-Mail'), u.ohne_mail],
    [
      'ohne_personenname',
      t('admin.neue_nutzer.ue_ohne_personenname', 'Ohne Vor-/Nachname (Service-Konten)'),
      u.ohne_personenname,
    ],
    [
      'deaktiviert_in_entra',
      t('admin.neue_nutzer.ue_deaktiviert_in_entra', 'In Entra deaktiviert'),
      u.deaktiviert_in_entra,
    ],
    ['testnutzer', t('admin.neue_nutzer.ue_testnutzer', 'Testnutzer'), u.testnutzer],
  ];
  return (
    <div>
      <div>
        {t(
          'admin.neue_nutzer.kennzahlen',
          '{{neu}} neu · {{geaendert}} geändert · {{reaktiviert}} reaktiviert · {{deaktiviert}} deaktiviert · {{uebersprungen}} übersprungen',
          {
            neu: erg.neu,
            geaendert: erg.geaendert,
            reaktiviert: erg.reaktiviert,
            deaktiviert: erg.deaktiviert,
            uebersprungen: summe,
          },
        )}
      </div>
      {summe > 0 && (
        <details className="text-xs text-[color:var(--ink-3)]">
          <summary className="cursor-pointer">
            {t('admin.neue_nutzer.ue_titel', 'Übersprungen nach Grund')}
          </summary>
          <ul className="ml-4 list-disc">
            {gruende
              .filter(([, , n]) => n > 0)
              .map(([key, label, n]) => (
                <li key={key}>
                  {label}: {n}
                </li>
              ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function NamenListe({
  titel,
  namen,
  hinweis,
  warnung = false,
}: {
  titel: string;
  namen: string[];
  /** Kurze Erklärung über der Liste (z. B. warum ausgesetzt). */
  hinweis?: string;
  warnung?: boolean;
}) {
  if (namen.length === 0) return null;
  return (
    <details
      className={`text-xs ${warnung ? 'text-[color:var(--warn)]' : 'text-[color:var(--ink-3)]'}`}
    >
      <summary className="cursor-pointer">
        {titel} ({namen.length})
      </summary>
      {hinweis && <p className="ml-4 mt-0.5 text-[color:var(--ink-3)]">{hinweis}</p>}
      <ul className="ml-4 list-disc">
        {namen.map((n, i) => (
          // Namen sind nicht eindeutig (Gleichnamige), die Reihenfolge ist stabil.
          // biome-ignore lint/suspicious/noArrayIndexKey: Liste ist statisch pro Lauf-Ergebnis
          <li key={`${n}-${i}`}>{n}</li>
        ))}
      </ul>
    </details>
  );
}

function NeueNutzerListe() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user: sessionUser } = useSession();
  const actor = sessionUser ?? ADMIN_LOADING_FALLBACK_ACTOR;
  const [erledigte, setErledigte] = useState(false);
  const query = useQuery({
    queryKey: [...LISTE_KEY, erledigte],
    queryFn: () => api.listNeueNutzer(erledigte),
  });
  const invalidate = useInvalidateNachRollenaenderung();
  const { assign, unassign } = useFunctionRoleMutations(invalidate);

  const pruefen = useMutation({
    mutationFn: ({ userId, zurueck }: { userId: string; zurueck: boolean }) =>
      zurueck ? api.nimmNeuGeprueftZurueck(userId) : api.setzeNeuGeprueft(userId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: LISTE_KEY }),
    onError: (err) =>
      toast.error(
        t('admin.neue_nutzer.geprueft_fehler', 'Status konnte nicht geändert werden'),
        err instanceof Error ? err.message : undefined,
      ),
  });

  const grundLabel: Record<NeuerNutzerErledigtGrund, string> = {
    geprueft: t('admin.neue_nutzer.grund_geprueft', 'geprüft'),
    rolle: t('admin.neue_nutzer.grund_rolle', 'Rolle vergeben'),
    pl: t('admin.neue_nutzer.grund_pl', 'PL auf offenem Projekt'),
  };
  const items: NeuerNutzer[] = query.data?.items ?? [];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium text-[color:var(--ink)]">
          {t('admin.neue_nutzer.liste_titel', 'Neue Nutzer')}
        </h3>
        <label className="flex items-center gap-1.5 text-xs text-[color:var(--ink-2)]">
          <input
            type="checkbox"
            checked={erledigte}
            onChange={(e) => setErledigte(e.target.checked)}
          />
          {t('admin.neue_nutzer.erledigte_anzeigen', 'Erledigte anzeigen')}
        </label>
      </div>
      {query.isLoading && (
        <p className="text-sm text-[color:var(--ink-3)]">{t('admin.loading', 'Lade…')}</p>
      )}
      {query.isError && (
        <p className="text-sm text-[color:var(--danger)]">
          {t('admin.neue_nutzer.laden_fehler', 'Die Liste konnte nicht geladen werden.')}
        </p>
      )}
      {query.data && (
        <div className="bg-[var(--surface)] border border-[color:var(--line)] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-2)] text-left text-xs uppercase tracking-wide text-[color:var(--ink-3)]">
              <tr>
                <th className="px-3 py-2">{t('admin.neue_nutzer.col_name', 'Name')}</th>
                <th className="px-3 py-2">{t('admin.neue_nutzer.col_email', 'E-Mail')}</th>
                <th className="px-3 py-2">{t('admin.neue_nutzer.col_seit', 'Seit')}</th>
                <th className="px-3 py-2">{t('admin.neue_nutzer.col_anlass', 'Anlass')}</th>
                <th className="px-3 py-2">{t('admin.neue_nutzer.col_rollen', 'Rollen')}</th>
                <th className="px-3 py-2 text-right">
                  {t('admin.neue_nutzer.col_aktion', 'Aktion')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--line-soft)]">
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-6 text-center text-sm text-[color:var(--ink-4)] italic"
                  >
                    {t('admin.neue_nutzer.leer', 'Keine offenen neuen Nutzer.')}
                  </td>
                </tr>
              )}
              {items.map((u) => (
                <tr key={u.user_id} className={u.erledigt_grund ? 'opacity-80' : undefined}>
                  <td className="px-3 py-2 font-medium text-[color:var(--ink)]">
                    {anzeigeName(u, t)}
                  </td>
                  <td className="px-3 py-2 text-xs text-[color:var(--ink-3)]">{u.email}</td>
                  <td className="px-3 py-2 text-xs text-[color:var(--ink-3)] whitespace-nowrap">
                    {formatZeitpunkt(u.neu_seit, i18n.language, 'datum')}
                  </td>
                  <td className="px-3 py-2">
                    <span className="px-1.5 py-0.5 rounded bg-[var(--surface-3)] text-[color:var(--ink-2)] text-[10px] font-medium">
                      {u.neu_anlass === 'reaktiviert'
                        ? t('admin.neue_nutzer.anlass_reaktiviert', 'reaktiviert')
                        : t('admin.neue_nutzer.anlass_neu', 'neu')}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <FunctionRoleEditor
                      user={{ ...u, is_auto_pl: u.ist_pl }}
                      actor={actor}
                      isSelf={u.user_id === actor.user_id}
                      inaktiv={!u.active}
                      onAssign={(role, scope_segment) =>
                        assign.mutate({ userId: u.user_id, role, scope_segment })
                      }
                      onUnassign={(rid) => unassign.mutate(rid)}
                    />
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {u.erledigt_grund ? (
                      <div className="flex items-center justify-end gap-2">
                        <span className="px-1.5 py-0.5 rounded bg-[var(--surface-2)] text-[color:var(--ink-3)] text-[10px] font-medium">
                          {grundLabel[u.erledigt_grund]}
                        </span>
                        {u.erledigt_grund === 'geprueft' && (
                          <button
                            type="button"
                            onClick={() => pruefen.mutate({ userId: u.user_id, zurueck: true })}
                            disabled={pruefen.isPending}
                            className="text-xs text-[color:var(--ink-3)] hover:text-[color:var(--primary)] px-2 py-1 rounded disabled:opacity-50"
                          >
                            {t('admin.neue_nutzer.zuruecknehmen', 'Zurücknehmen')}
                          </button>
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => pruefen.mutate({ userId: u.user_id, zurueck: false })}
                        disabled={pruefen.isPending}
                        className="text-xs px-2 py-1 rounded border border-[color:var(--line)] hover:bg-[var(--surface-2)] disabled:opacity-50"
                      >
                        {t('admin.neue_nutzer.geprueft', 'Geprüft')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
